/**
 * The second and third arts of every school past Wudang, and the wandering arts (วิชายุทธจักร)
 * anyone may learn. Plain data in the shape of `data/skills.ts`, which folds both lists into
 * SKILLS and MARTIAL_ARTS — add moves and arts here exactly as there.
 *
 * Balance follows the first arts: a school's second art (250) is a touch stronger than its first,
 * the third (600) a touch more again. Every art keeps the eight-move shape: a quick strike, a
 * guard-break (1 s cooldown, no qi, a wind-up), area and gap-closing moves, a self buff or heal,
 * and a rage ultimate that cannot be blocked.
 */
import type { MartialArtDef, SkillDef } from './skills';

export const EXTRA_SKILLS: readonly SkillDef[] = [
  // ── หินระฆัง: ฝ่ามือก้องระฆัง ──
  {
    id: 'pp-wave',
    name: 'ฝ่ามือระฆังส่งคลื่น',
    glyph: '波',
    kind: 'attack',
    qi: 12,
    cooldown: 2.2,
    windup: 0,
    range: 3.5,
    power: 1.1,
    fx: 'vajra',
    desc: 'ส่งคลื่นฝ่ามือสีทองไปไกลสามช่องครึ่ง ใช้ขัดจังหวะศัตรูที่กำลังชาร์จท่าทำลายจากระยะห่าง',
  },
  {
    id: 'pp-shatter',
    name: 'ฝ่ามือทลายศิลา',
    glyph: '碑',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.5,
    range: 2,
    power: 1.45,
    stun: 1.7,
    onGuardBreak: { status: 'sunder' },
    fx: 'quake',
    desc: 'ท่าทำลาย ฝ่ามือทุบลงดุจทุบศิลาจารึก ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติด "เกราะร้าว" รับความเสียหายเพิ่มขึ้น',
  },
  {
    id: 'pp-thousand',
    name: 'หัตถ์พันเงาระฆัง',
    glyph: '千',
    kind: 'attack',
    qi: 22,
    cooldown: 6,
    windup: 0,
    range: 2,
    power: 0.62,
    hits: 3,
    fx: 'palm',
    desc: 'ฝ่ามือนับพันซัดต่อเนื่องสามครั้ง ราวพระโพธิสัตว์พันกร',
  },
  {
    id: 'pp-calm',
    name: 'ฝ่ามือสะกดมาร',
    glyph: '鎮',
    kind: 'attack',
    qi: 24,
    cooldown: 9,
    windup: 0,
    range: 3,
    power: 0.95,
    applies: { status: 'shackle' },
    fx: 'vajra',
    desc: 'ฝ่ามือสะกดจิตมาร ศัตรูถูกตรึง เคลื่อนที่ช้าลงมากและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'pp-dharma',
    name: 'ธรรมจักรสะเทือนพสุธา',
    glyph: '法',
    kind: 'attack',
    qi: 26,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.55,
    aoe: 2.4,
    power: 1,
    applies: { status: 'sunder' },
    fx: 'quake',
    desc: 'ตบฝ่ามือลงพื้น ธรรมจักรสะเทือนรอบตัว ศัตรูโดยรอบติด "เกราะร้าว"',
  },
  {
    id: 'pp-lotus',
    name: 'บัวทองประทับนั่ง',
    glyph: '蓮',
    kind: 'heal',
    qi: 0,
    cooldown: 15,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.28,
    fx: 'calm',
    desc: 'นั่งสมาธิบนบัวทองชั่วครู่ ฟื้นพลังชีวิต 28% ในสามวินาที',
  },
  {
    id: 'pp-vow',
    name: 'ปณิธานโพธิสัตว์',
    glyph: '願',
    kind: 'buff',
    qi: 0,
    cooldown: 15,
    windup: 0,
    range: 0,
    power: 0,
    cleanse: true,
    self: { status: 'vow-guard' },
    fx: 'bell',
    desc: 'ตั้งปณิธานมั่น สลายสถานะที่จำกัดการเคลื่อนที่ แล้วตั้งรับรอ 8 วินาที ถ้ารับท่าได้ ลมปราณห่อกาย รับความเสียหายลดลง 30% และฟื้นพลังชีวิตเล็กน้อย ใช้ได้แม้ถูกทำให้มึน',
  },
  {
    id: 'pp-tathagata',
    name: 'ฝ่ามือระฆังครอบฟ้า',
    glyph: '鐘',
    kind: 'power',
    qi: 0,
    cooldown: 13,
    windup: 0,
    range: 0,
    power: 1.6,
    rage: 10,
    aoe: 3.2,
    hitStun: 1,
    fx: 'bell',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ฝ่ามือทองยักษ์ครอบลงรอบตัว ศัตรูโดยรอบมึน 1 วินาที ตั้งรับไม่ได้',
  },
  // ── หินระฆัง: ดาบค้อนช่างใหญ่ ──
  {
    id: 'fm-subdue',
    name: 'กระบี่สยบมาร',
    glyph: '伏',
    kind: 'attack',
    qi: 12,
    cooldown: 2.5,
    windup: 0,
    range: 2.1,
    power: 1.3,
    fx: 'sabre',
    desc: 'ฟันกระบี่หนักลงตรงๆ ไร้ลูกเล่น แต่หนักแน่นดุจภูผา',
  },
  {
    id: 'fm-heavy',
    name: 'กระบี่ระฆังเหล็ก',
    glyph: '鑄',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.55,
    range: 2.1,
    power: 1.7,
    stun: 1.8,
    onGuardBreak: { status: 'sunder' },
    fx: 'quake',
    desc: 'ท่าทำลาย ทุ่มน้ำหนักทั้งกายลงบนกระบี่ พลังแท้ไม่ต้องอาศัยคม ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติด "เกราะร้าว"',
  },
  {
    id: 'fm-cross',
    name: 'กระบี่กากบาทสะกดมาร',
    glyph: '十',
    kind: 'attack',
    qi: 20,
    cooldown: 5,
    windup: 0,
    range: 2.1,
    power: 0.78,
    hits: 2,
    fx: 'chain',
    desc: 'ฟันขวางแล้วผ่าตั้งเป็นกากบาท สองครั้งติดกัน',
  },
  {
    id: 'fm-wheel',
    name: 'กงล้อพิทักษ์ธรรม',
    glyph: '護',
    kind: 'attack',
    qi: 26,
    cooldown: 8,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.55,
    aoe: 2.4,
    power: 1.1,
    fx: 'whirl',
    desc: 'หมุนกระบี่เป็นกงล้อรอบกาย ฟันศัตรูทุกตัวในรัศมี 2 ช่องครึ่ง',
  },
  {
    id: 'fm-stride',
    name: 'พุทธะเหยียบมาร',
    glyph: '踐',
    kind: 'attack',
    qi: 22,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.2,
    dash: 'target',
    hitStun: 0.5,
    fx: 'sabre',
    desc: 'ก้าวเดียวถึงตัวศัตรูแล้วฟันกดลง ศัตรูมึน 0.5 วินาที',
  },
  {
    id: 'fm-bell',
    name: 'ระฆังทองครอบกาย',
    glyph: '罩',
    kind: 'buff',
    qi: 0,
    cooldown: 15,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'bell-guard' },
    fx: 'bell',
    desc: 'ตั้งรับรอ 8 วินาที ถ้ารับท่าได้ ระฆังทองครอบกายทั้งร่าง รับความเสียหายลดลงมาก และได้ความโกรธ',
  },
  {
    id: 'fm-zen',
    name: 'ฌานหนึ่งเดียว',
    glyph: '禪',
    kind: 'heal',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.3,
    fx: 'calm',
    desc: 'ปักกระบี่ลงพื้นแล้วเข้าฌาน ฟื้นพลังชีวิต 30% ในสามวินาที',
  },
  {
    id: 'fm-vanquish',
    name: 'พันพุทธะพิชิตมาร',
    glyph: '魔',
    kind: 'power',
    qi: 0,
    cooldown: 13,
    windup: 0,
    range: 2.6,
    power: 2.6,
    rage: 10,
    hitStun: 1.2,
    applies: { status: 'sunder' },
    fx: 'quake',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ฟันกระบี่หนักลงเพียงครั้งเดียว แผ่นดินแยก ศัตรูมึน 1.2 วินาทีและติด "เกราะร้าว" ตั้งรับไม่ได้',
  },
  // ── ทางโคมเหล็ก: หัตถ์ล็อกโซ่ทาง ──
  {
    id: 'ah-grip',
    name: 'หัตถ์ล็อกข้อ',
    glyph: '扣',
    kind: 'attack',
    qi: 10,
    cooldown: 2,
    windup: 0,
    range: 1.9,
    power: 1.15,
    applies: { status: 'slow' },
    fx: 'grab',
    desc: 'คว้าข้อมือศัตรูแล้วบิด ศัตรูเคลื่อนที่ช้าลง',
  },
  {
    id: 'ah-throw',
    name: 'ทุ่มลงพื้น',
    glyph: '摔',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.4,
    range: 2,
    power: 1.35,
    stun: 2,
    fx: 'grab',
    desc: 'ท่าทำลาย ชาร์จสั้น จับตัวศัตรูทุ่มลงพื้น การ์ดแตกและมึนงงนาน',
  },
  {
    id: 'ah-chain',
    name: 'โซ่ตรวนราชโองการ',
    glyph: '鎖',
    kind: 'attack',
    qi: 22,
    cooldown: 8,
    windup: 0,
    range: 5,
    power: 1,
    dash: 'target',
    applies: { status: 'shackle' },
    fx: 'chain',
    desc: 'พุ่งเข้าหาเป้าหมายแล้วคล้องโซ่ตรวน ศัตรูเคลื่อนที่ช้าลงมากและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'ah-seal',
    name: 'จี้จุดสะกด',
    glyph: '穴',
    kind: 'attack',
    qi: 20,
    cooldown: 12,
    windup: 0,
    range: 2,
    power: 0.6,
    applies: { status: 'silence' },
    fx: 'seal',
    desc: 'จี้จุดชีพจรนักโทษ ศัตรูใช้กระบวนท่าไม่ได้ 2 วินาที',
  },
  {
    id: 'ah-sweep',
    name: 'กวาดล้มทั้งแถว',
    glyph: '掃',
    kind: 'attack',
    qi: 24,
    cooldown: 8,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.5,
    aoe: 2.2,
    power: 0.95,
    applies: { status: 'slow' },
    fx: 'quake',
    desc: 'เตะกวาดต่ำรอบตัว ศัตรูโดยรอบล้มลุกคลุกคลานและเคลื่อนที่ช้า',
  },
  {
    id: 'ah-interrogate',
    name: 'ไต่สวนด้วยหมัด',
    glyph: '審',
    kind: 'attack',
    qi: 26,
    cooldown: 10,
    windup: 0,
    range: 2,
    power: 0.68,
    hits: 3,
    hitStun: 0.4,
    fx: 'palm',
    desc: 'หมัดสามครั้งติดกันใส่จุดอ่อน หมัดแรกทำให้ศัตรูชะงัก 0.4 วินาที',
  },
  {
    id: 'ah-decree',
    name: 'ราชโองการสั่งจับ',
    glyph: '令',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'decree-guard' },
    fx: 'aura',
    desc: 'ชูป้ายราชโองการตั้งรับรอ 8 วินาที ถ้ารับท่าได้ สร้างความเสียหายรุนแรงขึ้น 25% 6 วินาที',
  },
  {
    id: 'ah-imperial',
    name: 'หัตถ์มังกรราชันย์',
    glyph: '帝',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 2.4,
    power: 2.2,
    rage: 10,
    hitStun: 1.5,
    applies: { status: 'shackle' },
    fx: 'vajra',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 หัตถ์มังกรตะครุบจับกุม ศัตรูมึน 1.5 วินาทีแล้วถูกตรวน ตั้งรับไม่ได้',
  },
  // ── ทางโคมเหล็ก: ดาบชักเร็วโคมเหล็ก ──
  {
    id: 'sp-flash',
    name: 'ดาบโคมแวบ',
    glyph: '燈',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2.1,
    power: 1.15,
    applies: { status: 'thunder-daze' },
    fx: 'sabre',
    desc: 'ชักดาบแวบเดียวแล้วเก็บเข้าฝัก ประกายอัสนีแล่นตามคม ศัตรูติดอัสนีชา 1 ชั้น ครบ 3 ชั้นมึนงง',
  },
  {
    id: 'sp-pierce',
    name: 'ดาบเจาะเกราะเหล็ก',
    glyph: '貫',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2.1,
    power: 1.5,
    stun: 1.6,
    onGuardBreak: { status: 'thunder-daze' },
    fx: 'sabre',
    desc: 'ท่าทำลาย แทงปลายดาบทะลุเกราะ ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติดอัสนีชา',
  },
  {
    id: 'sp-petals',
    name: 'สะเก็ดไส้ตะเกียงร่วง',
    glyph: '芯',
    kind: 'attack',
    qi: 22,
    cooldown: 6,
    windup: 0,
    range: 2.2,
    power: 0.56,
    hits: 3,
    fx: 'chain',
    desc: 'ชักดาบ-เก็บดาบสามครั้งติดกัน คมพลิ้วดุจสะเก็ดไส้ตะเกียงร่วง',
  },
  {
    id: 'sp-bloom',
    name: 'พายุสะเก็ดอัสนี',
    glyph: '焰',
    kind: 'attack',
    qi: 28,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.5,
    aoe: 2.4,
    power: 1.05,
    applies: { status: 'thunder-daze' },
    fx: 'whirl',
    desc: 'เลือกพื้นที่ ชักดาบหมุนเป็นพายุสะเก็ดอัสนี ทุกคนในวงติดอัสนีชา',
  },
  {
    id: 'sp-hunt',
    name: 'เงาดาบไล่ล่า',
    glyph: '追',
    kind: 'attack',
    qi: 30,
    cooldown: 12,
    windup: 0,
    range: 6,
    aoe: 6,
    blink: 0.3,
    power: 0.7,
    hits: 3,
    fx: 'chain',
    desc: 'หายตัวไปปรากฏข้างศัตรูแล้วฟัน สามครั้ง ไล่ฟันศัตรูในระยะ 6 ช่องทีละตัว ระหว่างวาปไม่มีใครแตะต้องได้',
  },
  {
    id: 'sp-verdict',
    name: 'ตัดสินโทษ',
    glyph: '判',
    kind: 'attack',
    qi: 30,
    cooldown: 10,
    windup: 0,
    range: 2.1,
    power: 0.9,
    perTargetStack: { status: 'thunder-daze', bonus: 0.5 },
    fx: 'chain',
    desc: 'ชักดาบตัดสินโทษ ยิ่งศัตรูติดอัสนีชามากยิ่งแรง ใช้ชั้นอัสนีชาทั้งหมด ถ้าศัตรูเปิดช่องจะชักดาบจากสามทิศแล้วฟ้าผ่าปิดท้าย ทิ้งอัสนีชาไว้ 2 ชั้น',
  },
  {
    id: 'sp-loyal',
    name: 'ใจภักดีไม่หวั่น',
    glyph: '忠',
    kind: 'buff',
    qi: 0,
    cooldown: 13,
    windup: 0,
    range: 0,
    power: 0,
    cleanse: true,
    self: { status: 'loyal-guard' },
    fx: 'aura',
    desc: 'สลัดพันธนาการด้วยใจภักดี ลบสถานะที่จำกัดการเคลื่อนที่ แล้วตั้งรับรอ 8 วินาที ถ้ารับท่าได้ เคลื่อนที่เร็วขึ้นมากและได้ลมปราณคืน ใช้ได้แม้ถูกทำให้มึน',
  },
  {
    id: 'sp-decree',
    name: 'ดาบอัสนีราชโองการ',
    glyph: '御',
    kind: 'power',
    qi: 0,
    cooldown: 13,
    windup: 0,
    range: 6,
    power: 0.85,
    rage: 10,
    ground: true,
    land: 0.6,
    aoe: 3,
    hits: 3,
    hitStun: 1,
    fx: 'bolt',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 เลือกพื้นที่ในระยะ 6 ช่อง ชี้ดาบเรียกอัสนีฟาดลงสามครั้ง ศัตรูในวงมึน 1 วินาที ตั้งรับไม่ได้',
  },
  // ── จันทร์มัว: กระบี่จันทร์เสน่หา ──
  {
    id: 'ms-moon',
    name: 'แสงจันทร์ลูบไล้',
    glyph: '月',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2.2,
    power: 1.05,
    applies: { status: 'poison' },
    fx: 'shadow',
    desc: 'กระบี่ลูบไล้ดุจแสงจันทร์ ทิ้งพิษจันทร์มัวไว้ในบาดแผล',
  },
  {
    id: 'ms-veil',
    name: 'ม่านจันทร์ลวงตา',
    glyph: '紗',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2.1,
    power: 1.3,
    stun: 1.5,
    onGuardBreak: { status: 'charm' },
    fx: 'shadow',
    desc: 'ท่าทำลาย กระบี่ซ่อนหลังม่านแสงจันทร์ ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติดมนตร์จันทร์มัว',
  },
  {
    id: 'ms-longing',
    name: 'กระบี่คะนึงหา',
    glyph: '思',
    kind: 'attack',
    qi: 22,
    cooldown: 6,
    windup: 0,
    range: 2.2,
    power: 0.6,
    hits: 3,
    applies: { status: 'poison' },
    fx: 'chain',
    desc: 'แทงสามครั้งดุจคิดถึงไม่ขาดสาย แต่ละครั้งติดพิษ',
  },
  {
    id: 'ms-step',
    name: 'ก้าวเงาจันทร์',
    glyph: '影',
    kind: 'attack',
    qi: 20,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.05,
    dash: 'forward',
    applies: { status: 'poison' },
    fx: 'shadow',
    desc: 'พุ่งผ่านศัตรูดุจเงาจันทร์ ทุกตัวที่ขวางทางติดพิษ',
  },
  {
    id: 'ms-night',
    name: 'ราตรีห่มกาย',
    glyph: '夜',
    kind: 'attack',
    qi: 26,
    cooldown: 10,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.5,
    aoe: 2.4,
    power: 0.85,
    applies: { status: 'charm' },
    fx: 'shadow',
    desc: 'หมุนกระบี่ห่มความมืดรอบตัว ศัตรูโดยรอบติดมนตร์จันทร์มัว โจมตีเบาลงและช้าลง',
  },
  {
    id: 'ms-kiss',
    name: 'จุมพิตรัตติกาล',
    glyph: '吻',
    kind: 'attack',
    qi: 25,
    cooldown: 10,
    windup: 0,
    range: 2.2,
    power: 1,
    lifesteal: 0.45,
    fx: 'shadow',
    desc: 'ปลายกระบี่จุมพิตบาดแผล ดูดเลือดศัตรูกลับมาเกือบครึ่ง',
  },
  {
    id: 'ms-mirror',
    name: 'เงาจันทร์ในน้ำ',
    glyph: '鏡',
    kind: 'buff',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'mirror-guard' },
    fx: 'aura',
    desc: 'ร่างพร่าเลือนดุจเงาจันทร์ในน้ำตั้งรับรอ 8 วินาที ถ้ารับท่าได้ ติดดาบจันทร์ ติดคริติคอลง่ายขึ้น 5 วินาที',
  },
  {
    id: 'ms-eclipse',
    name: 'จันทราคราส',
    glyph: '蝕',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 7,
    aoe: 7,
    blink: 0.35,
    power: 0.55,
    rage: 10,
    hits: 6,
    hitStun: 0.8,
    perTargetStack: { status: 'poison', bonus: 0.3 },
    fx: 'shadow',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 จันทร์ดับ ร่างหายไปในความมืดแล้วปรากฏข้างศัตรู ฟันสูงสุด 6 ครั้ง ไล่ทุกตัวในระยะ 7 ช่อง ฟันแรกของแต่ละตัวทำให้แข็งทื่อ 0.8 วินาที ยิ่งติดพิษมากยิ่งแรง ตั้งรับไม่ได้',
  },
  // ── จันทร์มัว: เพลงหมู่เงาจันทร์ ──
  {
    id: 'dd-sleeve',
    name: 'แขนเสื้อพลิ้วมาร',
    glyph: '袖',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2.4,
    power: 1.1,
    applies: { status: 'poison' },
    fx: 'shadow',
    desc: 'สะบัดแขนเสื้อแพรยาวดุจแส้ ระยะไกลกว่ามือเปล่าทั่วไป ทิ้งพิษจันทร์มัวไว้',
  },
  {
    id: 'dd-twirl',
    name: 'หมุนกายสลายการ์ด',
    glyph: '轉',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.4,
    range: 2.1,
    power: 1.35,
    stun: 1.6,
    onGuardBreak: { status: 'poison' },
    fx: 'shadow',
    desc: 'ท่าทำลาย หมุนกายร่ายรำแล้วฟาดฝ่ามือ ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติดพิษ',
  },
  {
    id: 'dd-petals',
    name: 'ระบำกลีบท้อ',
    glyph: '瓣',
    kind: 'attack',
    qi: 28,
    cooldown: 10,
    windup: 0,
    range: 0,
    power: 0.48,
    aoe: 2.6,
    hits: 3,
    applies: { status: 'poison' },
    fx: 'shadow',
    desc: 'ร่ายรำหมุนสามรอบ กลีบท้อพิษปลิวรอบตัว ศัตรูโดยรอบโดนสามครั้งและติดพิษ',
  },
  {
    id: 'dd-drain',
    name: 'จุมพิตดูดวิญญาณ',
    glyph: '吸',
    kind: 'attack',
    qi: 26,
    cooldown: 10,
    windup: 0,
    range: 4,
    power: 0.95,
    dash: 'target',
    lifesteal: 0.4,
    fx: 'shadow',
    desc: 'ร่อนเข้าหาเป้าหมายแล้วดูดเลือดกลับมา 40% ของความเสียหาย',
  },
  {
    id: 'dd-mist',
    name: 'หมอกเสน่หา',
    glyph: '迷',
    kind: 'attack',
    qi: 28,
    cooldown: 12,
    windup: 0,
    range: 6,
    ground: true,
    land: 0.6,
    aoe: 2.8,
    power: 0.5,
    applies: { status: 'charm' },
    fx: 'shadow',
    desc: 'โปรยหมอกหอมหวานรอบตัว ศัตรูโดยรอบติดมนตร์จันทร์มัว',
  },
  {
    id: 'dd-trance',
    name: 'ร่ายรำเข้าภวังค์',
    glyph: '舞',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'trance-guard' },
    fx: 'aura',
    desc: 'ร่ายรำตั้งรับรอ 8 วินาที ถ้ารับท่าได้ จิตเข้าภวังค์ 6 วินาที โจมตีแรงขึ้น 15% และเคลื่อนที่เร็วขึ้น 15%',
  },
  {
    id: 'dd-dew',
    name: 'ดื่มน้ำค้างดอกท้อ',
    glyph: '露',
    kind: 'heal',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.25,
    fx: 'breath',
    desc: 'ดื่มน้ำค้างจากกลีบท้อ ฟื้นพลังชีวิต 25% ในสามวินาที',
  },
  {
    id: 'dd-heaven',
    name: 'เงาจันทร์ร่ายรำ',
    glyph: '天',
    kind: 'power',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 1.6,
    rage: 10,
    aoe: 3.2,
    hitStun: 1,
    lifesteal: 0.3,
    fx: 'shadow',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 เพลงหมู่เงาจันทร์รอบเดียวกลืนทั้งลาน ศัตรูโดยรอบมึน 1 วินาที ดูดเลือด 30% ของความเสียหาย ตั้งรับไม่ได้',
  },
  // ── หอหมึกสน: กระบี่ปลายแท่งหมึก ──
  {
    id: 'sf-finger',
    name: 'ปราณนิ้วกระบี่',
    glyph: '指',
    kind: 'attack',
    qi: 12,
    cooldown: 2.2,
    windup: 0,
    range: 4,
    power: 1,
    fx: 'seal',
    desc: 'ชี้นิ้วปล่อยปราณกระบี่ไปไกลสี่ช่อง ใช้ขัดจังหวะศัตรูที่กำลังชาร์จท่าทำลายได้',
  },
  {
    id: 'sf-pulse',
    name: 'จี้ชีพจรทลายลมปราณ',
    glyph: '脈',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2.2,
    power: 1.3,
    stun: 1.4,
    onGuardBreak: { status: 'silence' },
    fx: 'seal',
    desc: 'ท่าทำลาย จี้ชีพจรกลางการตั้งรับ การ์ดแตก และเมื่อการ์ดแตกศัตรูถูกปิดจุด ใช้กระบวนท่าไม่ได้',
  },
  {
    id: 'sf-six',
    name: 'หมึกปราณซัดสาย',
    glyph: '墨',
    kind: 'attack',
    qi: 28,
    cooldown: 10,
    windup: 0,
    range: 6,
    power: 0.55,
    volley: 6,
    fx: 'needles',
    desc: 'สะบัดพู่กันปล่อยปราณหมึกหลายสายพร้อมกัน พุ่งใส่ศัตรูในระยะ 6 ช่อง',
  },
  {
    id: 'sf-hold',
    name: 'ปิดจุดสะกดร่าง',
    glyph: '定',
    kind: 'attack',
    qi: 22,
    cooldown: 12,
    windup: 0,
    range: 4,
    power: 0.6,
    applies: { status: 'shackle' },
    fx: 'seal',
    desc: 'ดีดปราณปิดจุดขาจากระยะไกล ศัตรูเคลื่อนที่ช้าลงมากและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'sf-qin',
    name: 'พิณสะกดใจ',
    glyph: '琴',
    kind: 'attack',
    qi: 24,
    cooldown: 8,
    windup: 0,
    range: 6,
    ground: true,
    land: 0.5,
    aoe: 2.4,
    power: 0.85,
    applies: { status: 'slow' },
    fx: 'ink',
    desc: 'ดีดนิ้วกลางอากาศดุจดีดพิณ คลื่นเสียงแผ่รอบตัว ศัตรูโดยรอบเคลื่อนที่ช้า',
  },
  {
    id: 'sf-drift',
    name: 'ก้าวบัณฑิตเหินลม',
    glyph: '逸',
    kind: 'buff',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0,
    cleanse: true,
    self: { status: 'pulse-guard' },
    fx: 'aura',
    desc: 'ก้าวเบาดุจบัณฑิตเดินเล่น สลายสถานะที่จำกัดการเคลื่อนที่ แล้วตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้ ก้าวลื่นไหลเร็วขึ้นและได้ลมปราณคืน ใช้ได้แม้ถูกทำให้มึน',
  },
  {
    id: 'sf-water',
    name: 'จิตสงบดุจน้ำ',
    glyph: '水',
    kind: 'heal',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.25,
    fx: 'calm',
    desc: 'จิตนิ่งดุจผิวน้ำ ฟื้นพลังชีวิต 25% ในสามวินาที',
  },
  {
    id: 'sf-divine',
    name: 'อักษรปราณสะท้านฟ้า',
    glyph: '神',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 7,
    power: 0.45,
    rage: 10,
    volley: 12,
    hitStun: 0.5,
    fx: 'nine',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ตวัดอักษรปราณกลางอากาศสองบรรทัด สิบสองเส้นพุ่งใส่ศัตรูในระยะ 7 ช่อง ทุกสายที่โดนทำให้ชะงัก 0.5 วินาที ตั้งรับไม่ได้',
  },
  // ── หอหมึกสน: ขีดเดียวไร้ถ้อย ──
  {
    id: 'sg-line',
    name: 'หนึ่งขีดแม่นยำ',
    glyph: '一',
    kind: 'attack',
    qi: 12,
    cooldown: 3,
    windup: 0,
    range: 2.2,
    power: 1.45,
    applies: { status: 'late-wound' },
    fx: 'brush',
    desc: 'หนึ่งขีดที่แม่นดีกว่าร้อยขีดที่เร็ว ฟันเส้นเดียว แผลเปิดตามมาทีหลัง ถ้าศัตรูเปิดช่องจะขีดทะลุไปยืนหันหลังให้ แล้วแผลจึงเปิดออก',
  },
  {
    id: 'sg-verse',
    name: 'บทกวีทลายค่าย',
    glyph: '賦',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.5,
    range: 2.2,
    power: 1.5,
    stun: 1.6,
    onGuardBreak: { status: 'late-wound' },
    fx: 'brush',
    desc: 'ท่าทำลาย เขียนบทกวีกลางอากาศ ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติดแผลเปิดช้า',
  },
  {
    id: 'sg-rhyme',
    name: 'ลมหยุดหนึ่งขีด',
    glyph: '止',
    kind: 'attack',
    qi: 22,
    cooldown: 6,
    windup: 0,
    range: 2.2,
    power: 1.9,
    applies: { status: 'late-wound', stacks: 2 },
    fx: 'brush',
    desc: 'ยืนนิ่งกลั้นลมหายใจครู่หนึ่ง แล้วขีดกระบี่ครั้งเดียว ศัตรูติดแผลเปิดช้า 2 ชั้น',
  },
  {
    id: 'sg-stroke',
    name: 'พู่กันลากเส้นยาว',
    glyph: '劃',
    kind: 'attack',
    qi: 22,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.1,
    dash: 'forward',
    applies: { status: 'late-wound' },
    fx: 'brush',
    desc: 'พุ่งตัวเป็นเส้นยาวดุจลากพู่กัน ฟันทุกตัวที่ขวางทาง แผลเปิดตามหลัง',
  },
  {
    id: 'sg-scroll',
    name: 'คลี่ภาพทิวเขา',
    glyph: '山',
    kind: 'attack',
    qi: 26,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.55,
    aoe: 2.4,
    power: 1,
    applies: { status: 'late-wound' },
    fx: 'ink',
    desc: 'เลือกพื้นที่ คลี่ภาพทิวเขาหมึกลงพื้น ทุกคนในภาพถูกขีดเป็นเส้นเขา ติดแผลเปิดช้า',
  },
  {
    id: 'sg-insight',
    name: 'ตรัสรู้กลางหน้ากระดาษ',
    glyph: '覺',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'still-mind' },
    fx: 'aura',
    desc: 'จิตนิ่งดุจหน้ากระดาษ 6 วินาที เคลื่อนที่ช้าลง 20% แต่โจมตีแรงขึ้น 20%',
  },
  {
    id: 'sg-screen',
    name: 'ภาพวาดกำบังกาย',
    glyph: '屏',
    kind: 'buff',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'screen-guard' },
    fx: 'aura',
    desc: 'วาดฉากกั้นด้วยปราณหมึกตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้ จิตนิ่งไร้ถ้อย โจมตีแรงขึ้นแต่เคลื่อนที่ช้าลง และได้ลมปราณคืนเล็กน้อย',
  },
  {
    id: 'sg-masterpiece',
    name: 'ภาพปราชญ์สะท้านแผ่นดิน',
    glyph: '聖',
    kind: 'power',
    qi: 0,
    cooldown: 13,
    windup: 0,
    range: 0,
    power: 1,
    rage: 10,
    aoe: 3.2,
    hits: 2,
    hitStun: 1,
    fx: 'ink',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 วาดภาพทั้งแผ่นดินด้วยกระบี่ หมึกระเบิดสองระลอกรอบตัว ศัตรูโดยรอบมึน 1 วินาที ตั้งรับไม่ได้',
  },
  // ── ดาบชาด: คีมเล็บพยัคฆ์ไฟ ──
  {
    id: 'bc-rake',
    name: 'กรงเล็บฉีกเนื้อ',
    glyph: '抓',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 1.9,
    power: 1.05,
    applies: { status: 'bleed' },
    fx: 'blood',
    desc: 'ตะปบกรงเล็บฉีกเนื้อ ศัตรูโลหิตไหล',
  },
  {
    id: 'bc-crush',
    name: 'บดกระดูกพยัคฆ์',
    glyph: '虎',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2,
    power: 1.45,
    stun: 1.5,
    onGuardBreak: { status: 'bleed' },
    fx: 'blood',
    desc: 'ท่าทำลาย กระโจนบดการตั้งรับดุจพยัคฆ์ขย้ำ การ์ดแตกและโลหิตไหล',
  },
  {
    id: 'bc-maul',
    name: 'ขย้ำสองกรงเล็บ',
    glyph: '撕',
    kind: 'attack',
    qi: 20,
    cooldown: 5,
    windup: 0,
    range: 2,
    power: 0.76,
    hits: 2,
    applies: { status: 'bleed' },
    fx: 'blood',
    desc: 'ฉีกสองกรงเล็บติดกัน แต่ละครั้งทำให้โลหิตไหล ถ้าศัตรูเปิดช่องจะตะครุบกดลงพื้น ตะปบไม่ยั้ง พลิกตัวคำราม แล้วฉีกขึ้นจนกระเด็น',
  },
  {
    id: 'bc-pounce',
    name: 'พยัคฆ์ตะครุบ',
    glyph: '撲',
    kind: 'attack',
    qi: 22,
    cooldown: 7,
    windup: 0,
    range: 6,
    ground: true,
    land: 0.45,
    aoe: 1.8,
    power: 1.15,
    dash: 'target',
    applies: { status: 'bleed' },
    fx: 'blood',
    desc: 'กระโจนข้ามระยะหกช่องตะครุบเหยื่อ ศัตรูโลหิตไหล',
  },
  {
    id: 'bc-roar',
    name: 'พยัคฆ์คำรามเลือดเดือด',
    glyph: '嘯',
    kind: 'attack',
    qi: 28,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0.6,
    aoe: 3,
    hitStun: 0.7,
    fx: 'blood',
    desc: 'คำรามจนเลือดในกายเดือด ศัตรูโดยรอบมึน 0.7 วินาที',
  },
  {
    id: 'bc-feast',
    name: 'ลิ้มรสโลหิต',
    glyph: '食',
    kind: 'attack',
    qi: 25,
    cooldown: 10,
    windup: 0,
    range: 2,
    power: 0.85,
    lifesteal: 0.5,
    perTargetStack: { status: 'bleed', bonus: 0.35 },
    fx: 'blood',
    desc: 'ขย้ำแผลแล้วดูดเลือดกลับมาครึ่งหนึ่ง ยิ่งศัตรูโลหิตไหลมากยิ่งแรง',
  },
  {
    id: 'bc-fury',
    name: 'เลือดพยัคฆ์เดือด',
    glyph: '烈',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'fury-guard' },
    fx: 'blood',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้ เลือดพยัคฆ์เดือด โจมตีแรงขึ้น 30% ดูดเลือด 10% และได้ความโกรธ',
  },
  {
    id: 'bc-tiger',
    name: 'พยัคฆ์โลหิตขย้ำฟ้า',
    glyph: '猛',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 5,
    power: 0.8,
    rage: 10,
    dash: 'target',
    hits: 3,
    hitStun: 1,
    lifesteal: 0.2,
    fx: 'blood',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 กระโจนเข้าหาเป้าหมายแล้วขย้ำสามครั้ง ศัตรูมึน 1 วินาที ดูดเลือด 20% ตั้งรับไม่ได้',
  },
  // ── ดาบชาด: ดาบผลักผาชาด ──
  {
    id: 'rc-edge',
    name: 'คมไม่รู้หยุด',
    glyph: '鋒',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2.2,
    power: 1.2,
    fx: 'blood',
    desc: 'ดาบใหญ่ฟันหนักเดินหน้า ศัตรูถอยทีละก้าวแม้ตั้งรับไว้ ถ้าศัตรูเปิดช่องจะไล่ฟันห้าก้าว ดันถอยรวม 3 ช่อง',
  },
  {
    id: 'rc-split',
    name: 'ผ่าผาแดง',
    glyph: '劈',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.5,
    range: 2.2,
    power: 1.6,
    stun: 1.6,
    onGuardBreak: { status: 'sunder' },
    fx: 'blood',
    desc: 'ท่าทำลาย ผ่าดาบลงดุจผ่าผาแดง ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติด "เกราะร้าว"',
  },
  {
    id: 'rc-tide',
    name: 'คลื่นผาซัดฝั่ง',
    glyph: '潮',
    kind: 'attack',
    qi: 28,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.55,
    aoe: 2.6,
    power: 1.05,
    fx: 'blood',
    desc: 'เลือกพื้นที่ ฟันดาบลงพื้นเป็นคลื่นหินแตก ทุกคนในวงถูกดันถอยออกห่างผู้ใช้',
  },
  {
    id: 'rc-hunt',
    name: 'ดาบพุ่งดันผา',
    glyph: '獵',
    kind: 'attack',
    qi: 24,
    cooldown: 8,
    windup: 0,
    range: 6,
    power: 1,
    dash: 'target',
    fx: 'blood',
    desc: 'พุ่งเข้าหาเป้าหมายแล้วกระแทกด้วยสันดาบ ดันศัตรูถอยไป 2 ช่อง ตั้งรับไว้ก็ยังถูกดันถอย',
  },
  {
    id: 'rc-reap',
    name: 'กวาดเท้าผาถล่ม',
    glyph: '收',
    kind: 'attack',
    qi: 30,
    cooldown: 11,
    windup: 0,
    range: 0,
    power: 0.7,
    aoe: 2.6,
    fx: 'blood',
    desc: 'เหวี่ยงดาบกวาดต่ำรอบตัว ทุกคนโดยรอบล้ม ฟันโดนคนที่ล้มอยู่ได้',
  },
  {
    id: 'rc-cliff',
    name: 'ยืนหยัดบนผาแดง',
    glyph: '崖',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'cliff-guard' },
    fx: 'blood',
    desc: 'ปักดาบยืนหยัดดุจผาแดงตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้ ยืนมั่นไม่ถอย รับความเสียหายลดลงและโจมตีสวนแรงขึ้น',
  },
  {
    id: 'rc-drink',
    name: 'โลหิตคืนกาย',
    glyph: '赤',
    kind: 'heal',
    qi: 0,
    cooldown: 15,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.25,
    fx: 'calm',
    desc: 'เดินลมปราณให้เลือดที่เสียไปไหลคืนกาย ฟื้นพลังชีวิต 25% ในสามวินาที',
  },
  {
    id: 'rc-sea',
    name: 'ผาแดงถล่มทะเล',
    glyph: '海',
    kind: 'power',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0.42,
    rage: 10,
    aoe: 3,
    spin: { duration: 5, tick: 0.35 },
    fx: 'bloodstorm',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ฟันหนักเดินหน้าสามก้าว ดันทุกคนรอบตัวถอยไปทีละก้าว แล้วผ่าผาแดงถล่มใส่ ศัตรูกระเด็นไปทางขอบลาน ตั้งรับไม่ได้',
  },
  // ── ยุทธจักร: ฝ่ามือมังกรพายุ ──
  {
    id: 'dp-regret',
    name: 'มังกรพายุพุ่งชน',
    glyph: '衝',
    kind: 'attack',
    qi: 14,
    cooldown: 3,
    windup: 0,
    range: 2,
    power: 1.5,
    fx: 'palm',
    desc: 'ฝ่ามือแรกของวิชา พุ่งเข้าชนดุจลมพายุปะทะ หนักแต่ไม่เสียหลัก',
  },
  {
    id: 'dp-tail',
    name: 'หางพายุหวดกลับ',
    glyph: '尾',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2,
    power: 1.4,
    stun: 1.6,
    fx: 'grab',
    desc: 'ท่าทำลาย หมุนกายสะบัดฝ่ามือกลับหลังดุจหางมังกร ทลายการตั้งรับ',
  },
  {
    id: 'dp-field',
    name: 'พายุกลางทุ่ง',
    glyph: '野',
    kind: 'attack',
    qi: 24,
    cooldown: 8,
    windup: 0,
    range: 0,
    power: 0.95,
    aoe: 2.4,
    applies: { status: 'slow' },
    fx: 'quake',
    desc: 'ฝ่ามือกดลงพื้น พลังมังกรผุดขึ้นรอบตัว ศัตรูโดยรอบเคลื่อนที่ช้า',
  },
  {
    id: 'dp-leap',
    name: 'มังกรน้ำวนทะยาน',
    glyph: '渦',
    kind: 'attack',
    qi: 20,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.15,
    dash: 'target',
    fx: 'palm',
    desc: 'ทะยานจากที่ต่ำเข้าหาเป้าหมายแล้วซัดฝ่ามือ',
  },
  {
    id: 'dp-soar',
    name: 'พายุหมุนขึ้นฟ้า',
    glyph: '旋',
    kind: 'attack',
    qi: 26,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.55,
    aoe: 2.4,
    power: 1,
    fx: 'whirl',
    desc: 'กระโดดหมุนกลางอากาศแล้วฟาดฝ่ามือรอบทิศ โดนศัตรูทุกตัวในรัศมี 2 ช่องครึ่ง',
  },
  {
    id: 'dp-scales',
    name: 'เกล็ดมังกรคุ้มกาย',
    glyph: '鱗',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'scale-guard' },
    fx: 'aura',
    desc: 'ลมปราณแข็งดุจเกล็ดมังกรตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้ เกล็ดมังกรขึ้นเต็มกาย รับความเสียหายลดลงและดูดเลือดคืน',
  },
  {
    id: 'dp-hidden',
    name: 'มังกรนิ่งในเมฆ',
    glyph: '靜',
    kind: 'heal',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.25,
    fx: 'breath',
    desc: 'นิ่งดุจมังกรในเมฆก่อนพายุ เก็บลมปราณฟื้นพลังชีวิต 25% ในสามวินาที',
  },
  {
    id: 'dp-eighteen',
    name: 'ฝ่ามือมังกรพายุสะท้านฟ้า',
    glyph: '嵐',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 4,
    power: 0.85,
    rage: 10,
    hits: 3,
    hitStun: 1,
    fx: 'vajra',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ฝ่ามือมังกรซ้อนสามชั้นพุ่งใส่เป้าหมายในระยะ 4 ช่อง ศัตรูมึน 1 วินาที ตั้งรับไม่ได้',
  },
  // ── ยุทธจักร: อาวุธลับพันดารา ──
  {
    id: 'hw-dart',
    name: 'ลูกดอกเงา',
    glyph: '鏢',
    kind: 'attack',
    qi: 10,
    cooldown: 2,
    windup: 0,
    range: 6,
    power: 0.55,
    volley: 2,
    fx: 'needles',
    desc: 'ซัดลูกดอกสองดอกใส่ศัตรูในระยะ 6 ช่อง',
  },
  {
    id: 'hw-snare',
    name: 'ตาข่ายเข็มพิษ',
    glyph: '網',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2.2,
    power: 1.3,
    stun: 1.5,
    onGuardBreak: { status: 'poison' },
    fx: 'pierce',
    desc: 'ท่าทำลาย สะบัดตาข่ายเข็มคลุมการตั้งรับ การ์ดแตก และเมื่อการ์ดแตกศัตรูติดพิษ',
  },
  {
    id: 'hw-knives',
    name: 'มีดบินตัดเงา',
    glyph: '刀',
    kind: 'attack',
    qi: 20,
    cooldown: 6,
    windup: 0,
    range: 5,
    power: 0.5,
    volley: 3,
    applies: { status: 'bleed' },
    fx: 'needles',
    desc: 'ซัดมีดบินสามเล่ม ศัตรูที่โดนโลหิตไหล',
  },
  {
    id: 'hw-rain',
    name: 'ฝนเข็มพันดารา',
    glyph: '雨',
    kind: 'attack',
    qi: 28,
    cooldown: 10,
    windup: 0,
    range: 6,
    power: 0.4,
    volley: 8,
    applies: { status: 'poison' },
    fx: 'needles',
    desc: 'โปรยเข็มพิษแปดเล่มดุจห่าฝน กระจายใส่ศัตรูทุกตัวในระยะ 6 ช่อง',
  },
  {
    id: 'hw-caltrops',
    name: 'โปรยตะปูเรือใบ',
    glyph: '蒺',
    kind: 'attack',
    qi: 22,
    cooldown: 10,
    windup: 0,
    range: 6,
    ground: true,
    land: 0.45,
    aoe: 2.4,
    power: 0.45,
    applies: { status: 'chill' },
    fx: 'needles',
    desc: 'โปรยตะปูเรือใบใต้เท้าศัตรู ผู้ที่เหยียบช้าลงมากและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'hw-smoke',
    name: 'ระเบิดควันหลบเงา',
    glyph: '煙',
    kind: 'buff',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0,
    cleanse: true,
    self: { status: 'smoke-guard' },
    fx: 'aura',
    desc: 'ปาระเบิดควันแล้วหายตัว สลายสถานะที่จำกัดการเคลื่อนที่ แล้วตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้ ซ่อนกายในควัน เร็วขึ้นและคริติคอลสูงขึ้น ใช้ได้แม้ถูกทำให้มึน',
  },
  {
    id: 'hw-aim',
    name: 'จิตเล็งเป้า',
    glyph: '瞄',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'focus' },
    fx: 'aura',
    desc: 'กลั้นหายใจเล็งจุดตาย โอกาสคริติคอล +25% 6 วินาที',
  },
  {
    id: 'hw-stars',
    name: 'หมื่นดาราสังหาร',
    glyph: '萬',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 7,
    power: 0.45,
    rage: 10,
    volley: 12,
    hitStun: 0.5,
    fx: 'needles',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ซัดอาวุธลับทั้งหมดที่มีออกไปพร้อมกัน สิบสองชิ้นพุ่งใส่ศัตรูในระยะ 7 ช่อง ทุกชิ้นที่โดนทำให้ชะงัก 0.5 วินาที ตั้งรับไม่ได้',
  },
];

/** Arts in the order they are listed: each school's later arts, then the wandering ones. */
export const EXTRA_ARTS: readonly MartialArtDef[] = [
  {
    id: 'hum-palm',
    name: 'ฝ่ามือก้องระฆัง',
    school: 'หินระฆัง',
    schoolId: 'bell-stone',
    learnCost: 250,
    requires: 'bell-fist',
    weapon: 'unarmed',
    element: 'hard',
    damage: 'internal',
    desc: 'ฝ่ามือภายในของช่างหล่อระฆังผู้ชำนาญ ส่งคลื่นก้องไปไกล ตรึงศัตรูให้หยุดนิ่ง และทำให้เกราะศัตรูร้าว',
    skills: [
      'pp-wave',
      'pp-shatter',
      'pp-thousand',
      'pp-calm',
      'pp-dharma',
      'pp-lotus',
      'pp-vow',
      'pp-tathagata',
    ],
    basic: { power: 0.9, range: 1.8, rate: 0.7 },
  },
  {
    id: 'founder-sabre',
    name: 'ดาบค้อนช่างใหญ่',
    school: 'หินระฆัง',
    schoolId: 'bell-stone',
    learnCost: 600,
    requires: 'hum-palm',
    weapon: 'sword',
    element: 'hard',
    damage: 'external',
    desc: 'ดาบค้อนของช่างใหญ่ หนักแน่นไร้ลูกเล่น พลังแท้ไม่ต้องอาศัยคม ตีทีเดียวให้ศัตรูสะเทือนทั้งตัว',
    skills: [
      'fm-subdue',
      'fm-heavy',
      'fm-cross',
      'fm-wheel',
      'fm-stride',
      'fm-bell',
      'fm-zen',
      'fm-vanquish',
    ],
    basic: { power: 1.1, range: 2.05, rate: 0.8 },
  },
  {
    id: 'chain-hold',
    name: 'หัตถ์ล็อกโซ่ทาง',
    school: 'ทางโคมเหล็ก',
    schoolId: 'lantern-road',
    learnCost: 250,
    requires: 'lamp-hook-sabre',
    weapon: 'unarmed',
    element: 'yang',
    damage: 'external',
    desc: 'วิชามือเปล่าที่ผู้คุมทางใช้จับผู้ผิดกติกา ล็อกข้อ คล้องโซ่ ปิดจุด และไม่ปล่อยให้ใครหนีพ้น',
    skills: [
      'ah-grip',
      'ah-throw',
      'ah-chain',
      'ah-seal',
      'ah-sweep',
      'ah-interrogate',
      'ah-decree',
      'ah-imperial',
    ],
    basic: { power: 0.9, range: 1.7, rate: 0.65 },
  },
  {
    id: 'quick-draw-sabre',
    name: 'ดาบชักเร็วโคมเหล็ก',
    school: 'ทางโคมเหล็ก',
    schoolId: 'lantern-road',
    learnCost: 600,
    requires: 'chain-hold',
    weapon: 'sword',
    element: 'yang',
    damage: 'external',
    desc: 'ดาบของผู้คุมทางชั้นสูง ชักดาบแล้วเก็บเร็วดุจสายฟ้าแลบ ทุกคมทิ้งอัสนีชาไว้ หายตัวไล่ล่า แล้วตัดสินโทษผู้ที่ชาจนขยับไม่ได้',
    skills: [
      'sp-flash',
      'sp-pierce',
      'sp-petals',
      'sp-bloom',
      'sp-hunt',
      'sp-verdict',
      'sp-loyal',
      'sp-decree',
    ],
    basic: { power: 1.1, range: 2.05, rate: 0.75 },
  },
  {
    id: 'listening-blade',
    name: 'กระบี่ฟังใจจันทร์มัว',
    school: 'จันทร์มัว',
    schoolId: 'dim-moon',
    learnCost: 250,
    requires: 'quiet-palm',
    weapon: 'sword',
    element: 'yin',
    damage: 'internal',
    desc: 'เพลงกระบี่ที่หุบจันทร์มัวถอดจากกระบี่จันทร์เสน่หา ฟังใจคนก่อนชักกระบี่ อ่อนดุจแสงจันทร์ แต่ทุกคมอ่านจังหวะศัตรูได้',
    skills: ['ms-moon', 'ms-veil', 'ms-longing', 'ms-step', 'ms-night', 'ms-kiss', 'ms-mirror', 'ms-eclipse'],
    basic: { power: 0.95, range: 2.05, rate: 0.7 },
  },
  {
    id: 'chorus',
    name: 'เพลงหมู่เงาจันทร์',
    school: 'จันทร์มัว',
    schoolId: 'dim-moon',
    learnCost: 600,
    requires: 'listening-blade',
    weapon: 'unarmed',
    element: 'yin',
    damage: 'internal',
    desc: 'เพลงหมู่ของหุบจันทร์มัว ยิ่งบรรเลงยิ่งเร็วยิ่งแรง เสียงพิณและระฆังมือกล่อมและกดทั้งลาน',
    skills: ['dd-sleeve', 'dd-twirl', 'dd-petals', 'dd-drain', 'dd-mist', 'dd-trance', 'dd-dew', 'dd-heaven'],
    basic: { power: 0.9, range: 1.8, rate: 0.6 },
  },
  {
    id: 'pulse-seal',
    name: 'กระบี่ปลายแท่งหมึก',
    school: 'หอหมึกสน',
    schoolId: 'pine-ink',
    learnCost: 250,
    requires: 'ink-slate',
    weapon: 'unarmed',
    element: 'soft',
    damage: 'internal',
    desc: 'ผู้จดบันทึกไม่ถือกระบี่ก็ยังมีกระบี่ ปล่อยปราณกระบี่จากปลายนิ้ว ปิดจุดชีพจรศัตรูจากระยะไกล',
    skills: ['sf-finger', 'sf-pulse', 'sf-six', 'sf-hold', 'sf-qin', 'sf-drift', 'sf-water', 'sf-divine'],
    basic: { power: 0.85, range: 1.8, rate: 0.6 },
  },
  {
    id: 'one-cut',
    name: 'ขีดเดียวไร้ถ้อย',
    school: 'หอหมึกสน',
    schoolId: 'pine-ink',
    learnCost: 600,
    requires: 'pulse-seal',
    weapon: 'sword',
    element: 'soft',
    damage: 'internal',
    desc: 'ยอดวิชาของหอหมึกสน นิ่งดุจรอยหมึกบนแผ่นศิลา หนึ่งขีดที่แม่นดีกว่าร้อยขีดที่เร็ว รอยที่ขีดไว้จะเปิดเป็นแผลเมื่อกระบี่หยุด',
    skills: [
      'sg-line',
      'sg-verse',
      'sg-rhyme',
      'sg-stroke',
      'sg-scroll',
      'sg-insight',
      'sg-screen',
      'sg-masterpiece',
    ],
    basic: { power: 1, range: 2.1, rate: 0.7 },
  },
  {
    id: 'tongs-claw',
    name: 'คีมเล็บพยัคฆ์ไฟ',
    school: 'ดาบชาด',
    schoolId: 'cinnabar',
    learnCost: 250,
    requires: 'heat-sabre',
    weapon: 'unarmed',
    element: 'yang',
    damage: 'external',
    desc: 'วิชามือเปล่าของศิษย์ดาบชาดยามดาบหลุดมือ คีมเล็บฉีกเนื้อดุจพยัคฆ์ ยิ่งไฟในกายร้อนยิ่งคลั่ง',
    skills: ['bc-rake', 'bc-crush', 'bc-maul', 'bc-pounce', 'bc-roar', 'bc-feast', 'bc-fury', 'bc-tiger'],
    basic: { power: 0.95, range: 1.7, rate: 0.65 },
  },
  {
    id: 'cliff-push-sabre',
    name: 'ดาบผลักผาชาด',
    school: 'ดาบชาด',
    schoolId: 'cinnabar',
    learnCost: 600,
    requires: 'tongs-claw',
    weapon: 'sword',
    element: 'yang',
    damage: 'external',
    desc: 'วิชาดาบที่ผู้ก่อตั้งสำนักถอดจากกระบี่บนผาแดง ฟันหนักเดินหน้าไม่รู้จักหยุด ทุกคมดันศัตรูถอยจนตกขอบผา',
    skills: ['rc-edge', 'rc-split', 'rc-tide', 'rc-hunt', 'rc-reap', 'rc-cliff', 'rc-drink', 'rc-sea'],
    basic: { power: 1.2, range: 2.15, rate: 0.9 },
  },
  {
    id: 'dragon-palm',
    name: 'ฝ่ามือมังกรพายุ',
    school: 'พเนจร',
    weapon: 'unarmed',
    element: 'hard',
    damage: 'external',
    learnCost: 500,
    desc: 'ฝ่ามือของคนเรือที่ฝ่าพายุมาทั้งชีวิต แข็งกร้าวตรงไปตรงมา ทุกฝ่ามือมีเสียงพายุคำราม ใครก็เรียนได้',
    skills: [
      'dp-regret',
      'dp-tail',
      'dp-field',
      'dp-leap',
      'dp-soar',
      'dp-scales',
      'dp-hidden',
      'dp-eighteen',
    ],
    basic: { power: 0.95, range: 1.7, rate: 0.7 },
  },
  {
    id: 'hidden-arts',
    name: 'อาวุธลับพันดารา',
    school: 'พเนจร',
    weapon: 'unarmed',
    element: 'yin',
    damage: 'external',
    learnCost: 500,
    desc: 'วิชาซัดอาวุธลับของนักฆ่าพเนจร ลูกดอก มีดบิน เข็มพิษ และตะปูเรือใบ ฆ่าจากระยะไกลก่อนศัตรูเข้าถึงตัว',
    skills: ['hw-dart', 'hw-snare', 'hw-knives', 'hw-rain', 'hw-caltrops', 'hw-smoke', 'hw-aim', 'hw-stars'],
    basic: { power: 0.85, range: 1.8, rate: 0.6 },
  },
];
